import React from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import AdminLoginDialog from "../components/admin/AdminLoginDialog";
import CreateNewsletterDialog from "../components/admin/CreateNewsletterDialog";
import { supabase } from "../utils/supabase";

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [authChecked, setAuthChecked] = React.useState(false);
  const [isLoginDialogOpen, setIsLoginDialogOpen] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [fetchError, setFetchError] = React.useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [isCreating, setIsCreating] = React.useState(false);
  const [removingId, setRemovingId] = React.useState(null);
  const [confirmRemoveId, setConfirmRemoveId] = React.useState(null);
  const [createError, setCreateError] = React.useState("");

  // Restore session on mount and listen for auth changes
  React.useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setIsAuthenticated(!!data.session);
      setAuthChecked(true);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = () => {
    setIsLoginDialogOpen(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const handleOpenCreateDialog = () => {
    setCreateError("");
    setIsCreateDialogOpen(true);
  };

  const handleCreateNewsletter = async ({
    title,
    description,
    type,
    imageFile,
  }) => {
    setIsCreating(true);
    setCreateError("");

    let imageUrl = null;

    if (imageFile) {
      const safeName = imageFile.name
        .replace(/[\\/]/g, "-")
        .replace(/\.\./g, "-")
        .replace(/[^a-zA-Z0-9._-]/g, "-")
        .replace(/\s+/g, "-")
        .toLowerCase();
      const filePath = `newsletters/${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("images")
        .upload(filePath, imageFile, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        setCreateError(uploadError.message || "Kunde inte ladda upp bilden.");
        setIsCreating(false);
        return;
      }

      const { data: publicUrlData } = supabase.storage
        .from("images")
        .getPublicUrl(filePath);
      imageUrl = publicUrlData?.publicUrl || null;
    }

    const { data, error } = await supabase
      .from("newsLetter")
      .insert([{ title, description, type, image_url: imageUrl }])
      .select("id, created_at, title, description, type, image_url")
      .single();

    if (error) {
      setCreateError(error.message || "Kunde inte skapa nyhetsbrevet.");
      setIsCreating(false);
      return;
    }

    setRows((current) => [data, ...current]);
    setIsCreating(false);
    setIsCreateDialogOpen(false);
  };

  const handleRemoveNewsletter = async (id) => {
    setFetchError("");
    setRemovingId(id);

    const { error } = await supabase.from("newsLetter").delete().eq("id", id);

    if (error) {
      setFetchError(error.message || "Kunde inte ta bort nyhetsbrevet.");
      setRemovingId(null);
      return;
    }

    setRows((current) => current.filter((row) => row.id !== id));
    setRemovingId(null);
    setConfirmRemoveId(null);
  };

  React.useEffect(() => {
    if (!isAuthenticated) return;

    let isMounted = true;

    const fetchNewsletters = async () => {
      setIsLoading(true);
      setFetchError("");

      const { data, error } = await supabase
        .from("newsLetter")
        .select("id, created_at, title, description, type, image_url")
        .order("created_at", { ascending: false });

      if (!isMounted) return;

      if (error) {
        setFetchError(error.message || "Kunde inte hämta nyhetsbrev.");
        setRows([]);
      } else {
        setRows(Array.isArray(data) ? data : []);
      }

      setIsLoading(false);
    };

    fetchNewsletters();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f3f6fb",
        px: "var(--site-gutter)",
        py: { xs: 4, md: 6 },
      }}
    >
      <AdminLoginDialog
        open={isLoginDialogOpen}
        onLogin={handleLogin}
        onClose={() => setIsLoginDialogOpen(false)}
      />
      <CreateNewsletterDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateNewsletter}
        isSubmitting={isCreating}
        error={createError}
      />
      <Dialog
        open={Boolean(confirmRemoveId)}
        onClose={() => {
          if (!removingId) setConfirmRemoveId(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Är du säker?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Detta kommer att ta bort nyhetsbrevet permanent.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setConfirmRemoveId(null)}
            disabled={Boolean(removingId)}
          >
            Avbryt
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={Boolean(removingId)}
            onClick={() => handleRemoveNewsletter(confirmRemoveId)}
          >
            {removingId ? "Tar bort..." : "Ta bort"}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ maxWidth: 1100, mx: "auto" }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/")}
          sx={{
            mb: 2,
            textTransform: "none",
            color: "#1a2440",
            fontWeight: 600,
          }}
        >
          Tillbaka till startsidan
        </Button>

        {!authChecked ? null : !isAuthenticated ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              p: { xs: 3, md: 4 },
              boxShadow: "0 14px 34px rgba(8, 18, 43, 0.08)",
              border: "1px solid rgba(11, 22, 54, 0.08)",
              textAlign: "center",
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Adminpanel
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Logga in för att hantera nyhetsbrev.
            </Typography>
            <Button
              variant="contained"
              onClick={() => setIsLoginDialogOpen(true)}
              sx={{ textTransform: "none" }}
            >
              Login
            </Button>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              p: { xs: 2.5, md: 4 },
              boxShadow: "0 14px 34px rgba(8, 18, 43, 0.08)",
              border: "1px solid rgba(11, 22, 54, 0.08)",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              sx={{ mb: 3 }}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  Adminpanel
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  Hantera nyhetsbrev
                </Typography>
              </Box>

              <Stack direction="row" spacing={1.2}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateDialog}
                  sx={{ textTransform: "none" }}
                >
                  Lägg till nyhetsbrev
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleSignOut}
                  sx={{ textTransform: "none" }}
                >
                  Logga ut
                </Button>
              </Stack>
            </Stack>

            {fetchError ? (
              <Typography color="error" sx={{ mb: 2 }}>
                {fetchError}
              </Typography>
            ) : null}

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2, overflowX: "auto" }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Skapad</TableCell>
                    <TableCell>Titel</TableCell>
                    <TableCell>Typ</TableCell>
                    <TableCell>Beskrivning</TableCell>
                    <TableCell>Bild</TableCell>
                    <TableCell align="right">Åtgärd</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="text.secondary">
                          Hämtar nyhetsbrev...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="text.secondary">
                          Inga nyhetsbrev än. Använd "Lägg till nyhetsbrev" för att skapa en artikel.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>
                          {row.created_at
                            ? new Date(row.created_at).toLocaleDateString(
                                "sv-SE",
                              )
                            : "-"}
                        </TableCell>
                        <TableCell>{row.title}</TableCell>
                        <TableCell>{row.type || "-"}</TableCell>
                        <TableCell>{row.description || "-"}</TableCell>
                        <TableCell>{row.image_url ? "Ja" : "Nej"}</TableCell>
                        <TableCell align="left">
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={removingId === row.id}
                            onClick={() => setConfirmRemoveId(row.id)}
                            sx={{
                              minWidth: 30,
                              width: 30,
                              height: 30,
                              borderRadius: "50%",
                              p: 0,
                              fontSize: "1.2rem",
                              lineHeight: 1,
                              textTransform: "none",
                            }}
                          >
                            -
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </Box>
  );
}

